// src/components/Paywall.jsx
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import robotColor from '../images/codiii-icon.svg';
import robotWhite from '../images/codiii-icon_white.svg';
import mascot from '../images/codiii-mascot.svg';

const PageWrap = styled.div`
  * { margin: 0; padding: 0; box-sizing: border-box; }
  min-height: 100vh;
  display: flex; align-items: center; justify-content: center;
  position: relative; overflow: hidden;
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #dc2626 100%);
`;

const RobotPattern = styled.div`
  position: absolute; inset: 0; pointer-events: none; z-index: 1;

  .robot-icon { position: absolute; width: 80px; height: 80px; opacity: 0.1; animation: float 20s infinite linear; }
  .robot-icon:nth-child(odd){ animation-direction: reverse; opacity: 0.08; }
  .robot-icon:nth-child(1){ top:10%; left:5%; animation-delay:0s; }
  .robot-icon:nth-child(2){ top:20%; right:8%; animation-delay:-3s; }
  .robot-icon:nth-child(3){ top:40%; left:12%; animation-delay:-6s; }
  .robot-icon:nth-child(4){ top:60%; right:15%; animation-delay:-9s; }
  .robot-icon:nth-child(5){ top:75%; left:20%; animation-delay:-12s; }
  .robot-icon:nth-child(6){ top:30%; left:50%; animation-delay:-15s; }
  .robot-icon:nth-child(7){ top:80%; right:30%; animation-delay:-18s; }
  .robot-icon:nth-child(8){ top:15%; left:75%; animation-delay:-2s; }

  @keyframes float {
    0%{transform:translateY(0) rotate(0)}
    25%{transform:translateY(-20px) rotate(90deg)}
    50%{transform:translateY(-40px) rotate(180deg)}
    75%{transform:translateY(-20px) rotate(270deg)}
    100%{transform:translateY(0) rotate(360deg)}
  }

  img { width:100%; height:100%; object-fit:contain; filter: drop-shadow(0 2px 8px rgba(0,0,0,0.1)); }
  @media (max-width: 480px){ .robot-icon { width:60px; height:60px; } }
`;

const Card = styled.div`
  position: relative; z-index: 5;
  width: 100%; max-width: 980px;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 42px;
  box-shadow: 0 25px 50px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1);
  animation: slideUp 0.6s ease-out;

  @keyframes slideUp {
    from { opacity:0; transform: translateY(24px) scale(0.985); }
    to { opacity:1; transform: translateY(0) scale(1); }
  }

  @media (max-width: 900px){ max-width: 520px; padding: 34px 24px; margin: 18px; }
`;

const LogoSection = styled.div`
  text-align: center; margin-bottom: 18px;

  .logo-container { display:inline-flex; align-items:center; justify-content:center; margin-bottom:10px; }
  .logo { width:58px; height:58px; margin-right:12px; }
  .brand-text { font-size:30px; font-weight:800; color:#1f2937; letter-spacing:-0.02em; }
  .brand-dots { color:#f97316; }
  .tagline { color:#6b7280; font-size:13px; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; margin-top:6px; }
`;

const H1 = styled.h1`
  font-size: 26px;
  font-weight: 800;
  color: #111827;
  text-align: center;
  margin: 6px 0 8px;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 15px;
  text-align: center;
  margin-bottom: 18px;
  line-height: 1.45;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 16px;

  @media (max-width: 900px){
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.button`
  text-align: left;
  width: 100%;
  border: 2px solid ${p => (p.$selected ? '#f97316' : '#e5e7eb')};
  background: ${p => (p.$selected ? 'rgba(249,115,22,0.08)' : '#fff')};
  border-radius: 18px;
  padding: 18px 18px;
  cursor: pointer;
  transition: all .18s ease;
  box-shadow: ${p => (p.$selected ? '0 10px 24px rgba(249,115,22,0.18)' : '0 8px 18px rgba(0,0,0,0.06)')};

  &:hover{
    transform: translateY(-1px);
    border-color: #f97316;
    box-shadow: 0 12px 28px rgba(249,115,22,0.22);
  }
`;

const PlanTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;

  .name { font-weight: 900; font-size: 16px; color: #111827; }
  .price { font-weight: 900; font-size: 16px; color: #111827; }
  .per { font-weight: 700; font-size: 12px; color: #6b7280; margin-left: 6px; }
`;

const TinyNote = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 800;
  color: #6b7280;
`;

const BulletList = styled.ul`
  list-style: none;
  margin-top: 10px;
  padding-left: 0;
  color: #374151;
  font-size: 13px;
  line-height: 1.55;

  li {
    margin: 6px 0;
    display: flex;
    gap: 8px;
    align-items: flex-start;
  }

  li:before {
    content: "✓";
    font-weight: 900;
    color: #f97316;
    margin-top: -1px;
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  flex-wrap: wrap;

  @media (max-width: 900px){
    justify-content: center;
  }
`;

const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;

  button {
    border: none;
    background: transparent;
    padding: 10px 12px;
    font-weight: 800;
    cursor: pointer;
    color: #6b7280;
    transition: all .15s ease;
    min-width: 124px;
  }

  button.active {
    background: rgba(249,115,22,0.14);
    color: #111827;
  }
`;

const PrimaryBtn = styled.button`
  width: 100%;
  margin-top: 16px;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  border: none;
  border-radius: 16px;
  color: #fff;
  padding: 16px 18px;
  font-size: 15px;
  font-weight: 900;
  cursor: pointer;
  transition: all .2s ease;
  box-shadow: 0 10px 24px rgba(249,115,22,0.30), 0 2px 8px rgba(0,0,0,0.10);

  &:hover { transform: translateY(-1px); box-shadow: 0 14px 28px rgba(249,115,22,0.36), 0 6px 16px rgba(0,0,0,0.12); }
  &:active { transform: translateY(0); }
`;
const SecondaryBtn = styled(PrimaryBtn)`
  background: rgba(255,255,255,0.92);
  color: #111827;
  border: 2px solid #e5e7eb;
  box-shadow: 0 8px 18px rgba(0,0,0,0.08);
  &:hover { transform: translateY(-1px); box-shadow: 0 12px 22px rgba(0,0,0,0.10); }
`;


const FinePrint = styled.div`
  margin-top: 10px;
  text-align: center;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
`;

const MiniBox = styled.div`
  margin-top: 14px;
  border-radius: 14px;
  background: rgba(17,24,39,0.04);
  border: 1px solid rgba(17,24,39,0.08);
  padding: 12px 12px;
  font-size: 12px;
  color: #374151;
  line-height: 1.5;
  word-break: break-word;
`;

export default function Paywall({ company }) {
  const [cycle, setCycle] = useState('monthly');     // monthly|annual
  const [plan, setPlan] = useState('starter');       // starter|studio|firm
  const [sandboxLoading, setSandboxLoading] = useState(false);
  const [sandboxError, setSandboxError] = useState('');


  const plans = useMemo(() => {
    // Stripe plan pricing from your screenshots
    const priceMap = {
      starter: { monthly: '$199', annual: '$1,990' },
      studio:  { monthly: '$300', annual: '$3,000' },
      firm:    { monthly: '$400', annual: '$4,000' },
    };

    const per = cycle === 'monthly' ? '/month' : '/year';

    return [
      {
        key: 'starter',
        name: 'CODiii Starter',
        price: priceMap.starter[cycle],
        per,
        annualNote: cycle === 'annual' ? 'Annual subscription (2 months free)' : '',
        bullets: [
          'Starter plan access',
          'Projects + docs + chats',
          'Core compliance workflow',
        ],
      },
      {
        key: 'studio',
        name: 'CODiii Studio',
        price: priceMap.studio[cycle],
        per,
        annualNote: cycle === 'annual' ? 'Annual subscription (2 months free)' : '',
        bullets: [
          'Studio plan access',
          'More capacity for teams',
          'Admin controls + invites',
        ],
      },
      {
        key: 'firm',
        name: 'CODiii Firm',
        price: priceMap.firm[cycle],
        per,
        annualNote: cycle === 'annual' ? 'Annual subscription (2 months free)' : '',
        bullets: [
          'Firm plan access',
          'Company-wide sharing',
          'Priority support baseline',
        ],
      },
    ];
  }, [cycle]);

  async function onContinue() {
  try {
    const idToken =
      localStorage.getItem("id_token") ||
      localStorage.getItem("idToken") ||
      localStorage.getItem("cognito_id_token");

    if (!idToken) {
      alert("Missing login token. Please sign in again.");
      return;
    }

    const res = await fetch(
      `${process.env.REACT_APP_BILLING_API}/billing/checkout-session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          plan,      // starter|studio|firm
          cycle,     // monthly|annual
          successUrl: `${window.location.origin}/billing/success`,
          cancelUrl: `${window.location.origin}/billing/cancel`,
        }),
      }
    );

    const data = await res.json();
    if (!res.ok) {
      alert(data?.error || "Failed to create checkout session");
      return;
    }

    if (!data?.url) {
      alert("No checkout URL returned");
      return;
    }

    window.location.href = data.url;
  } catch (e) {
    console.error(e);
    alert("Checkout failed. Check console + Lambda logs.");
  }
}
  async function onTrySandbox() {
    setSandboxError('');
    setSandboxLoading(true);

    try {
      const idToken =
        localStorage.getItem("id_token") ||
        localStorage.getItem("idToken") ||
        localStorage.getItem("cognito_id_token");

      if (!idToken) {
        setSandboxError("Missing login token. Please sign in again.");
        return;
      }

      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

      const res = await fetch(`${API_BASE}/api/auth/sandbox/activate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({}),
      });

      const text = await res.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch { /* ignore */ }

      if (!res.ok) {
        const msg =
          (data && (data.detail || data.error)) ||
          text ||
          "Sandbox activation failed";

        setSandboxError(String(msg));
        return;
      }

      window.dispatchEvent(new Event('auth-tokens-changed'));
      window.location.replace('/');


    } catch (e) {
      console.error(e);
      setSandboxError(e?.message || "Sandbox activation failed.");
    } finally {
      setSandboxLoading(false);
    }
  }



  return (
    <PageWrap>
      <RobotPattern>
        {[1,2,3,4,5,6,7,8].map((i) => (
          <div key={i} className="robot-icon">
            <img src={i <= 5 ? robotColor : robotWhite} alt="CODiii Robot" />
          </div>
        ))}
      </RobotPattern>

      <Card>
        <LogoSection>
          <div className="logo-container">
            <div className="logo"><img src={mascot} alt="CODiii Logo" /></div>
            <span className="brand-text">COD<span className="brand-dots">iii</span></span>
          </div>
          <div className="tagline">The Design & Construction Brain</div>
        </LogoSection>

        <H1>Choose a plan to continue</H1>
        <Subtitle>
          {company?.name
            ? <>Workspace detected: <b>{company.name}</b>. Unlock full access with a subscription.</>
            : <>No workspace yet. Subscribe to create your workspace and unlock the app.</>}
        </Subtitle>

        <Controls>
          <Segmented>
            <button
              type="button"
              className={cycle === 'monthly' ? 'active' : ''}
              onClick={() => setCycle('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              className={cycle === 'annual' ? 'active' : ''}
              onClick={() => setCycle('annual')}
            >
              Annual
            </button>
          </Segmented>

        </Controls>

        <Grid>
          {plans.map(p => (
            <PlanCard
              key={p.key}
              $selected={plan === p.key}
              onClick={() => setPlan(p.key)}
              type="button"
            >
              <PlanTitleRow>
                <div className="name">{p.name}</div>
                <div className="price">
                  {p.price}<span className="per">{p.per}</span>
                </div>
              </PlanTitleRow>

              {p.annualNote ? <TinyNote>{p.annualNote}</TinyNote> : null}

              <BulletList>
                {p.bullets.map(b => <li key={b}>{b}</li>)}
              </BulletList>
            </PlanCard>
          ))}
        </Grid>

        <PrimaryBtn onClick={onContinue}>
          Continue to Checkout
        </PrimaryBtn>
        <SecondaryBtn onClick={onTrySandbox} disabled={sandboxLoading} style={{ marginTop: 10 }}>
          {sandboxLoading ? 'Starting Sandbox…' : 'Try Sandbox (24 hours)'}
        </SecondaryBtn>

        {sandboxError ? (
          <MiniBox style={{ borderColor: 'rgba(185,28,28,0.25)', background: 'rgba(185,28,28,0.06)' }}>
            <b style={{ color: '#b91c1c' }}>Sandbox:</b> {sandboxError}
          </MiniBox>
        ) : null}

        <FinePrint>
          You’ll be redirected to Stripe to complete payment.
        </FinePrint>
      </Card>
    </PageWrap>
  );
}
